import { Filter } from 'pixi.js';
import { shader } from './shader.js';

/**
 * DirectionalMirrorFilter is a custom PIXI.js filter that creates a mirror effect on portions of a texture in a specified direction. 
 * This filter supports four reflection directions: left-to-right, right-to-left, top-to-bottom, and bottom-to-top.
 * 
 * Key Features:
 * - Dynamically applies mirroring effects based on the selected direction.
 * - Supports customizable amplitude, waveLength, and alpha values for additional effects.
 * - The `boundary` parameter defines the threshold for the mirroring effect in the selected direction.
 * - Optimized for performance by utilizing GLSL shaders for rendering.
 * 
 * Usage:
 * - Instantiate the filter with a specified direction (e.g., `REFLECTION_DIRECTION_TWO_WAY.FROM_LEFT_TO_RIGHT`).
 * - Optionally configure properties like `amplitude` and `boundary` for fine-tuning the effect.
 * - Add the filter to a PIXI.Sprite or PIXI.Container.
 * 
 * Notes:
 * - The shader logic implements separate functions for each mirroring direction, which are dynamically triggered based on uniforms like `mirrorLeftToRight`, `mirrorTopToBottom`, etc.
 * - Ensure that `REFLECTION_DIRECTION_TWO_WAY` is correctly imported and contains the four expected directions.
 */
export class DirectionalMirrorFilter extends Filter {
    constructor() {
        super(null, shader);
        this.uniforms.amplitude = new Float32Array(2);
        this.uniforms.waveLength = new Float32Array(2);
        this.uniforms.alpha = new Float32Array(2);
        this.uniforms.dimensions = new Float32Array(2);

        Object.assign(this, {
            mirror: true,
            boundary: 0.5,
            direction: 1,
            amplitude: [0, 0],
            waveLength: [10, 10],
            alpha: [1, 1],
            time: 0,
        });
    }

    apply(filterManager, input, output, clear) {
        this.uniforms.dimensions[0] = input.filterFrame.width;
        this.uniforms.dimensions[1] = input.filterFrame.height;

        this.uniforms.time = this.time;

        filterManager.applyFilter(this, input, output, clear);
    }

    set mirror(value) {
        this.uniforms.mirror = value;
    }

    get mirror() {
        return this.uniforms.mirror;
    }

    set amplitude(value) {
        this.uniforms.amplitude[0] = value[0];
        this.uniforms.amplitude[1] = value[1];
    }
    get amplitude() {
        return this.uniforms.amplitude;
    }

    set boundary(value) {
        this.uniforms.boundary = value;
    }
    get boundary() {
        return this.uniforms.boundary;
    }

    set direction(value) {
        this.uniforms.direction = value;
    }

    get direction() {
        return this.uniforms.direction;
    }

    set alpha(value) {
        this.uniforms.alpha[0] = value[0];
        this.uniforms.alpha[1] = value[1];
    }

    get alpha() {
        return this.uniforms.alpha;
    }

    set waveLength(value) {
        this.uniforms.waveLength[0] = value[0];
        this.uniforms.waveLength[1] = value[1];
    }
    get waveLength() {
        return this.uniforms.waveLength;
    }
}